// MOCK data for the project-view panels (Insights, Roadmap, Workload, Finance,
// Task List) — populated for the DEMO: Office "Life Connect", project "adb"
// (la relance/migration de la gestion immobilière par agents IA, d'après la
// Roadmap Life Connect du 2026-06-22). Every panel imports these deterministic
// fixtures so the views look populated and believable. NO Math.random /
// Date.now: all values are hardcoded so charts, gantt bars and cost rollups
// render identically every time. Currency is EUR throughout (client FR).
//
// Cast (mirrors the roadmap team) :
//   Humans  — Greg (direction/supervision tech), Jennifer (Product Owner —
//             oriente le produit), Agnès (comptable — valide compta/déclarations),
//             Khudolii (développeur — pilote les agents IA), Camille (juriste —
//             baux commerciaux & RGPD, ponctuel).
//   Agents  — squad DEV : Aria (architecte), Boris & Diego (implémenteurs),
//             Tess (QA), Raphaël (relecteur) ; squad SUPPORT : Théo (triage
//             bugs), Otto (astreinte / correction auto), Cassandre (agent IA
//             comptable, supervisé par Agnès).

// --- Members: humans AND agents (parity is load-bearing — AssigneeBadge keys off
// `kind`). hourly_rate (EUR) drives Finance cost rollups : humans carry standard
// professional rates ; agents a low "marginal"/token-cost proxy. avatar_hue feeds
// the avatar tint.
export const mockMembers = [
  { id: 'mem-greg', kind: 'human', display_name: 'Greg Horion', avatar_hue: 210, role: 'Direction / Tech', hourly_rate: 140 },
  { id: 'mem-jennifer', kind: 'human', display_name: 'Jennifer Bangui', avatar_hue: 330, role: 'Product Owner', hourly_rate: 120 },
  { id: 'mem-agnes', kind: 'human', display_name: 'Agnès Descamps', avatar_hue: 160, role: 'Comptable', hourly_rate: 110 },
  { id: 'mem-khudolii', kind: 'human', display_name: 'Andrii Khudolii', avatar_hue: 200, role: 'Développeur', hourly_rate: 95 },
  { id: 'mem-camille', kind: 'human', display_name: 'Camille Roux', avatar_hue: 280, role: 'Juriste', hourly_rate: 140 },
  // DEV squad (agents) — single first names (no descriptive titles)
  { id: 'agt-aria', kind: 'agent', display_name: 'Aria', avatar_hue: 265, role: 'Architect', hourly_rate: 18 },
  { id: 'agt-boris', kind: 'agent', display_name: 'Boris', avatar_hue: 25, role: 'Implementer', hourly_rate: 12 },
  { id: 'agt-diego', kind: 'agent', display_name: 'Diego', avatar_hue: 45, role: 'Implementer', hourly_rate: 12 },
  { id: 'agt-tess', kind: 'agent', display_name: 'Tess', avatar_hue: 95, role: 'QA / Tester', hourly_rate: 9 },
  { id: 'agt-raphael', kind: 'agent', display_name: 'Raphaël', avatar_hue: 290, role: 'Reviewer', hourly_rate: 14 },
  // SUPPORT squad (agents)
  { id: 'agt-theo', kind: 'agent', display_name: 'Théo', avatar_hue: 5, role: 'Bug Triage', hourly_rate: 8 },
  { id: 'agt-otto', kind: 'agent', display_name: 'Otto', avatar_hue: 190, role: 'Support / On-call', hourly_rate: 10 },
  { id: 'agt-cassandre', kind: 'agent', display_name: 'Cassandre', avatar_hue: 150, role: 'Accounting AI', hourly_rate: 11 },
];

// Quick lookup by id (used by cost rollups + assignee resolution).
export const membersById = Object.fromEntries(mockMembers.map((m) => [m.id, m]));

// --- Phases (ADR-026 flavored): the lifecycle a story moves through. Kept as the
// 4 canonical lanes the Roadmap groups stories under.
export const mockPhases = ['Discovery', 'Planning', 'Implementation', 'Review'];

// --- Missions: goals (one-shot outcomes) + routines (recurring). These map the
// roadmap's workstreams : socle déjà livré, comptabilité, baux (non comm. puis
// commerciaux), gestion réelle pilote, reprise/bascule, automatisations, support.
export const mockMissions = [
  { id: 'mis-socle', title: 'Socle technique & mise en production', type: 'goal', status: 'done', phase: 'Review' },
  { id: 'mis-compta', title: 'Comptabilité — fiabilisation & mise en prod', type: 'goal', status: 'in_progress', phase: 'Implementation' },
  { id: 'mis-baux-nc', title: 'Baux non commerciaux — développement', type: 'goal', status: 'in_progress', phase: 'Implementation' },
  { id: 'mis-baux-comm', title: 'Baux commerciaux — socle & cadrage', type: 'goal', status: 'planned', phase: 'Planning' },
  { id: 'mis-pilote-di', title: 'Gestion réelle — pilote Digital Immo (80 lots)', type: 'routine', status: 'in_progress', phase: 'Review' },
  { id: 'mis-reprise', title: 'Reprise de parc & migration des données', type: 'goal', status: 'planned', phase: 'Planning' },
  { id: 'mis-bascule', title: 'Reprise comptable & bascule (1ᵉʳ janvier)', type: 'goal', status: 'planned', phase: 'Discovery' },
  { id: 'mis-automations', title: 'Automatisations & IA', type: 'routine', status: 'in_progress', phase: 'Implementation' },
  { id: 'mis-support', title: 'Support & corrections', type: 'routine', status: 'in_progress', phase: 'Review' },
];

// Mission title by id — keeps each story's `mission_title` in lockstep with the
// mission list (no copy-paste drift).
const missionTitle = Object.fromEntries(mockMissions.map((m) => [m.id, m.title]));

// Resolve a subset of members from ids → full member objects (for assignees).
function pick(ids) {
  return ids.map((id) => membersById[id]).filter(Boolean);
}

// Story factory — fills the verbose shared shape so the array below stays
// readable. `est` = estimated hours ; `logged` = logged hours (→ seconds).
function story(o) {
  return {
    id: o.id,
    key: o.key,
    title: o.title,
    phase: o.phase,
    description_md: o.description_md,
    status: o.status,
    priority: o.priority,
    progress: o.progress,
    assignees: pick(o.assignees),
    labels: o.labels,
    start_date: o.start_date,
    due_on: o.due_on,
    estimated_hours: o.est,
    logged_seconds: Math.round((o.logged ?? 0) * 3600),
    mission_id: o.mission_id,
    mission_title: missionTitle[o.mission_id],
    parent_id: o.parent_id ?? null,
    depends_on: o.depends_on ?? [],
    fixed_cost: o.fixed_cost ?? 0,
    reporter_id: o.reporter_id ?? 'mem-greg',
    created_at: o.created_at,
    updated_at: o.updated_at,
  };
}

const L = {
  compta: { name: 'comptabilité', color: '#f59e0b' },
  baux: { name: 'baux', color: '#6366f1' },
  technique: { name: 'technique', color: '#0ea5e9' },
  reprise: { name: 'reprise', color: '#8b5cf6' },
  bug: { name: 'bug', color: '#ef4444' },
  juridique: { name: 'juridique', color: '#ec4899' },
  auto: { name: 'automatisation', color: '#10b981' },
  doc: { name: 'doc', color: '#64748b' },
};

// --- Stories (~38). key = LC-N. ~4 are subtasks (parent_id set). statuses /
// priorities / phases / dates spread across Apr–Sep 2026 so charts + gantt look
// alive (the panels pin "today" to 2026-06-13). estimated vs logged hours vary so
// Finance + Workload have real signal. fixed_cost stubs non-labor cost (juriste
// ponctuel, infra, outillage migration). depends_on wires the gantt arrows.
export const mockStories = [
  // ===== mis-socle (done — already rebuilt & reviewed, not yet field-validated)
  story({
    id: 'st-1', key: 'LC-1', title: 'Extranet propriétaires — portail',
    phase: 'Review',
    description_md: 'Portail propriétaires : documents, comptes-rendus, suivi des lots. Module Extranet (déjà développé à 100 %).',
    status: 'done', priority: 'high', progress: 100,
    assignees: ['agt-aria', 'agt-boris'], labels: [L.technique, L.doc],
    start_date: '2026-04-06', due_on: '2026-04-24', est: 40, logged: 41,
    mission_id: 'mis-socle', reporter_id: 'mem-greg',
    created_at: '2026-04-02T09:00:00Z', updated_at: '2026-04-24T16:00:00Z',
  }),
  story({
    id: 'st-2', key: 'LC-2', title: 'Module Communication — modèles e-mail & courriers',
    phase: 'Implementation',
    description_md: 'Génération des courriers/emails types (relances, avis, convocations). Module Communication (100 % développé).',
    status: 'done', priority: 'medium', progress: 100,
    assignees: ['agt-diego'], labels: [L.technique],
    start_date: '2026-04-13', due_on: '2026-04-30', est: 24, logged: 22,
    mission_id: 'mis-socle', reporter_id: 'mem-jennifer',
    created_at: '2026-04-08T09:00:00Z', updated_at: '2026-04-30T16:00:00Z',
  }),
  story({
    id: 'st-3', key: 'LC-3', title: 'Infrastructure Cloudflare & accès sécurisés',
    phase: 'Review',
    description_md: 'Plateforme cloud unique (Workers/D1), CI déploiement + prévisualisation, gestion des accès. Action immédiate n°3.',
    status: 'done', priority: 'high', progress: 100,
    assignees: ['mem-greg', 'agt-boris'], labels: [L.technique],
    start_date: '2026-04-20', due_on: '2026-05-08', est: 36, logged: 40,
    mission_id: 'mis-socle', fixed_cost: 2500, reporter_id: 'mem-greg',
    created_at: '2026-04-15T09:00:00Z', updated_at: '2026-05-08T16:00:00Z',
  }),

  // ===== mis-compta (60 % développé mais ~85 % de validation experte requise)
  story({
    id: 'st-4', key: 'LC-4', title: 'Plan comptable & écritures',
    phase: 'Implementation',
    description_md: 'Plan comptable immobilier, journaux, écritures automatiques. À valider par l’expert-comptable.',
    status: 'in_review', priority: 'high', progress: 90,
    assignees: ['agt-cassandre', 'mem-agnes'], labels: [L.compta],
    start_date: '2026-05-11', due_on: '2026-06-05', est: 48, logged: 44,
    mission_id: 'mis-compta', reporter_id: 'mem-agnes',
    created_at: '2026-05-06T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),
  story({
    id: 'st-5', key: 'LC-5', title: 'Appels de loyers — génération',
    phase: 'Implementation',
    description_md: 'Génération des appels de loyers et charges, échéancier, export. Cœur du cycle de gestion.',
    status: 'in_progress', priority: 'high', progress: 65,
    assignees: ['agt-boris', 'agt-cassandre'], labels: [L.compta],
    start_date: '2026-05-18', due_on: '2026-06-19', est: 40, logged: 26,
    mission_id: 'mis-compta', depends_on: ['st-4'], reporter_id: 'mem-agnes',
    created_at: '2026-05-12T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-6', key: 'LC-6', title: 'Comptes-rendus de gestion aux propriétaires',
    phase: 'Implementation',
    description_md: 'Reddition de comptes périodique : revenus, charges, honoraires, solde propriétaire.',
    status: 'in_progress', priority: 'medium', progress: 40,
    assignees: ['agt-cassandre', 'mem-agnes'], labels: [L.compta],
    start_date: '2026-05-25', due_on: '2026-06-26', est: 28, logged: 11,
    mission_id: 'mis-compta', depends_on: ['st-4'], reporter_id: 'mem-agnes',
    created_at: '2026-05-20T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-7', key: 'LC-7', title: 'Déclarations fiscales (TVA, précompte)',
    phase: 'Planning',
    description_md: 'Déclarations périodiques, TVA sur baux commerciaux, précompte immobilier. Validation comptable + juriste.',
    status: 'planned', priority: 'high', progress: 0,
    assignees: ['agt-cassandre', 'mem-agnes', 'mem-camille'], labels: [L.compta, L.juridique],
    start_date: '2026-06-22', due_on: '2026-07-17', est: 30, logged: 0,
    mission_id: 'mis-compta', depends_on: ['st-4'], reporter_id: 'mem-agnes',
    created_at: '2026-06-02T09:00:00Z', updated_at: '2026-06-02T16:00:00Z',
  }),
  story({
    id: 'st-8', key: 'LC-8', title: 'Rapprochement bancaire',
    phase: 'Planning',
    description_md: 'Import des relevés, rapprochement automatique des encaissements loyers vs écritures.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['agt-cassandre'], labels: [L.compta, L.auto],
    start_date: '2026-07-06', due_on: '2026-07-24', est: 22, logged: 0,
    mission_id: 'mis-compta', depends_on: ['st-5'], reporter_id: 'mem-agnes',
    created_at: '2026-06-04T09:00:00Z', updated_at: '2026-06-04T16:00:00Z',
  }),
  story({
    id: 'st-9', key: 'LC-9', title: 'Anomalie compta pilote — correction en boucle',
    phase: 'Review',
    description_md: 'Anomalies révélées lors de la tentative pilote (Digital Immo) l’an dernier : solde propriétaire incohérent. Reproduire + corriger.',
    status: 'blocked', priority: 'urgent', progress: 30,
    assignees: ['agt-tess', 'agt-boris'], labels: [L.compta, L.bug],
    start_date: '2026-05-28', due_on: '2026-06-05', est: 14, logged: 9,
    mission_id: 'mis-compta', depends_on: ['st-5'], reporter_id: 'mem-agnes',
    created_at: '2026-05-26T09:00:00Z', updated_at: '2026-06-10T16:00:00Z',
  }),

  // ===== mis-baux-nc (le vrai verrou fonctionnel : ~9 % développé)
  story({
    id: 'st-10', key: 'LC-10', title: 'Modèle de bail non commercial',
    phase: 'Implementation',
    description_md: 'Structure du bail d’habitation : parties, lot, loyer, charges, dépôt, durée. Orienté par le PO.',
    status: 'in_progress', priority: 'high', progress: 55,
    assignees: ['agt-aria', 'mem-jennifer'], labels: [L.baux],
    start_date: '2026-05-19', due_on: '2026-06-16', est: 32, logged: 18,
    mission_id: 'mis-baux-nc', reporter_id: 'mem-jennifer',
    created_at: '2026-05-14T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-11', key: 'LC-11', title: 'Cycle de vie du bail (entrée / sortie)',
    phase: 'Implementation',
    description_md: 'Entrée, renouvellement, congé, sortie. États des lieux. Pilotage des transitions.',
    status: 'in_progress', priority: 'high', progress: 35,
    assignees: ['agt-boris', 'mem-jennifer'], labels: [L.baux],
    start_date: '2026-05-26', due_on: '2026-06-30', est: 36, logged: 13,
    mission_id: 'mis-baux-nc', depends_on: ['st-10'], reporter_id: 'mem-jennifer',
    created_at: '2026-05-20T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-11a', key: 'LC-11a', title: 'État des lieux d’entrée',
    phase: 'Implementation',
    description_md: 'Sous-tâche : grille d’état des lieux d’entrée, photos, signatures.',
    status: 'in_progress', priority: 'medium', progress: 50,
    assignees: ['agt-boris'], labels: [L.baux],
    start_date: '2026-05-27', due_on: '2026-06-13', est: 10, logged: 5,
    mission_id: 'mis-baux-nc', parent_id: 'st-11', reporter_id: 'mem-jennifer',
    created_at: '2026-05-21T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-11b', key: 'LC-11b', title: 'État des lieux de sortie + comparatif',
    phase: 'Implementation',
    description_md: 'Sous-tâche : état des lieux de sortie, comparatif entrée/sortie, retenues sur dépôt.',
    status: 'planned', priority: 'medium', progress: 0,
    assignees: ['agt-diego'], labels: [L.baux],
    start_date: '2026-06-16', due_on: '2026-06-30', est: 10, logged: 0,
    mission_id: 'mis-baux-nc', parent_id: 'st-11', reporter_id: 'mem-jennifer',
    created_at: '2026-05-21T09:00:00Z', updated_at: '2026-06-01T16:00:00Z',
  }),
  story({
    id: 'st-12', key: 'LC-12', title: 'Indexation des loyers',
    phase: 'Implementation',
    description_md: 'Révision annuelle indexée (indice santé), avis d’indexation, historique.',
    status: 'in_review', priority: 'medium', progress: 85,
    assignees: ['agt-diego', 'mem-agnes'], labels: [L.baux, L.compta],
    start_date: '2026-05-26', due_on: '2026-06-12', est: 18, logged: 17,
    mission_id: 'mis-baux-nc', depends_on: ['st-10'], reporter_id: 'mem-jennifer',
    created_at: '2026-05-22T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),
  story({
    id: 'st-13', key: 'LC-13', title: 'Quittances & avis d’échéance',
    phase: 'Planning',
    description_md: 'Édition des quittances de loyer et avis d’échéance, envoi automatique via le module Communication.',
    status: 'planned', priority: 'medium', progress: 0,
    assignees: ['agt-boris'], labels: [L.baux],
    start_date: '2026-06-23', due_on: '2026-07-10', est: 16, logged: 0,
    mission_id: 'mis-baux-nc', depends_on: ['st-11'], reporter_id: 'mem-jennifer',
    created_at: '2026-06-03T09:00:00Z', updated_at: '2026-06-03T16:00:00Z',
  }),
  story({
    id: 'st-14', key: 'LC-14', title: 'Régularisation des charges',
    phase: 'Planning',
    description_md: 'Décompte annuel des charges récupérables, régularisation, ventilation par lot.',
    status: 'backlog', priority: 'high', progress: 0,
    assignees: ['agt-cassandre', 'mem-agnes'], labels: [L.baux, L.compta],
    start_date: '2026-07-13', due_on: '2026-08-07', est: 26, logged: 0,
    mission_id: 'mis-baux-nc', depends_on: ['st-12'], reporter_id: 'mem-agnes',
    created_at: '2026-06-05T09:00:00Z', updated_at: '2026-06-05T16:00:00Z',
  }),

  // ===== mis-baux-comm (mobilise surtout gestion + juriste)
  story({
    id: 'st-15', key: 'LC-15', title: 'Spécification du bail commercial (3-6-9)',
    phase: 'Discovery',
    description_md: 'Cadrage juridique du bail commercial : durée, destination, dépôt de garantie, cession. Cadrage juriste ponctuel.',
    status: 'planned', priority: 'high', progress: 0,
    assignees: ['agt-aria', 'mem-camille', 'mem-jennifer'], labels: [L.baux, L.juridique],
    start_date: '2026-06-23', due_on: '2026-07-15', est: 28, logged: 0,
    mission_id: 'mis-baux-comm', fixed_cost: 6400, reporter_id: 'mem-jennifer',
    created_at: '2026-06-08T09:00:00Z', updated_at: '2026-06-08T16:00:00Z',
  }),
  story({
    id: 'st-16', key: 'LC-16', title: 'Clauses & révisions (ILC / ILAT)',
    phase: 'Planning',
    description_md: 'Indices commerciaux (ILC/ILAT), révision triennale, clause d’échelle mobile.',
    status: 'backlog', priority: 'high', progress: 0,
    assignees: ['agt-aria', 'mem-camille'], labels: [L.baux, L.juridique],
    start_date: '2026-07-16', due_on: '2026-08-07', est: 24, logged: 0,
    mission_id: 'mis-baux-comm', depends_on: ['st-15'], reporter_id: 'mem-jennifer',
    created_at: '2026-06-09T09:00:00Z', updated_at: '2026-06-09T16:00:00Z',
  }),
  story({
    id: 'st-17', key: 'LC-17', title: 'Refacturation charges & taxes commerciales',
    phase: 'Planning',
    description_md: 'Refacturation des charges, taxe foncière et assurances au preneur commercial.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['agt-cassandre', 'mem-agnes'], labels: [L.baux, L.compta],
    start_date: '2026-08-10', due_on: '2026-08-28', est: 20, logged: 0,
    mission_id: 'mis-baux-comm', depends_on: ['st-15'], reporter_id: 'mem-agnes',
    created_at: '2026-06-10T09:00:00Z', updated_at: '2026-06-10T16:00:00Z',
  }),
  story({
    id: 'st-18', key: 'LC-18', title: 'Pilote Bertolo (5 lots, 2 comm.) — paramétrage',
    phase: 'Discovery',
    description_md: 'Le portefeuille commercial le plus simple — paramétrage du pilote des baux commerciaux.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['mem-jennifer', 'agt-boris'], labels: [L.baux],
    start_date: '2026-08-03', due_on: '2026-08-21', est: 18, logged: 0,
    mission_id: 'mis-baux-comm', depends_on: ['st-15'], reporter_id: 'mem-jennifer',
    created_at: '2026-06-11T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),

  // ===== mis-pilote-di (routine : gestion en parallèle des 2 logiciels, 3 cycles)
  story({
    id: 'st-19', key: 'LC-19', title: 'Gestion en parallèle Digital Immo — cycle 1',
    phase: 'Review',
    description_md: 'Premier cycle complet (appel de loyers + compte-rendu) saisi en parallèle SPI / Life Connect.',
    status: 'done', priority: 'high', progress: 100,
    assignees: ['mem-jennifer', 'mem-agnes'], labels: [L.compta, L.baux],
    start_date: '2026-05-04', due_on: '2026-05-30', est: 24, logged: 27,
    mission_id: 'mis-pilote-di', reporter_id: 'mem-jennifer',
    created_at: '2026-05-01T09:00:00Z', updated_at: '2026-05-30T16:00:00Z',
  }),
  story({
    id: 'st-20', key: 'LC-20', title: 'Gestion en parallèle Digital Immo — cycle 2',
    phase: 'Review',
    description_md: 'Second cycle de gestion réelle, corrections issues du cycle 1 intégrées.',
    status: 'in_progress', priority: 'high', progress: 50,
    assignees: ['mem-jennifer', 'mem-agnes'], labels: [L.compta, L.baux],
    start_date: '2026-06-01', due_on: '2026-06-27', est: 24, logged: 12,
    mission_id: 'mis-pilote-di', depends_on: ['st-19'], reporter_id: 'mem-jennifer',
    created_at: '2026-05-28T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-21', key: 'LC-21', title: 'Écarts SPI vs Life Connect — rapprochement',
    phase: 'Review',
    description_md: 'Comparer les sorties des deux logiciels lot par lot ; qualifier et router chaque écart.',
    status: 'in_progress', priority: 'high', progress: 45,
    assignees: ['agt-tess', 'mem-agnes'], labels: [L.bug, L.compta],
    start_date: '2026-05-25', due_on: '2026-06-08', est: 20, logged: 14,
    mission_id: 'mis-pilote-di', depends_on: ['st-19'], reporter_id: 'mem-agnes',
    created_at: '2026-05-22T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),
  story({
    id: 'st-22', key: 'LC-22', title: 'Cycle 3 sans anomalie — validation',
    phase: 'Review',
    description_md: 'Critère de bascule : 3 cycles complets sans anomalie sur le pilote.',
    status: 'planned', priority: 'medium', progress: 0,
    assignees: ['mem-jennifer', 'mem-agnes'], labels: [L.compta],
    start_date: '2026-07-01', due_on: '2026-07-28', est: 24, logged: 0,
    mission_id: 'mis-pilote-di', depends_on: ['st-20', 'st-21'], reporter_id: 'mem-jennifer',
    created_at: '2026-06-02T09:00:00Z', updated_at: '2026-06-02T16:00:00Z',
  }),

  // ===== mis-reprise (Reprise de parc : 10 % développé, ~80 % expert)
  story({
    id: 'st-23', key: 'LC-23', title: 'Import des lots & propriétaires (4 parcs)',
    phase: 'Planning',
    description_md: 'Import des ~784 lots (Digital Immo 80, Bertolo 5, ImmoCore 230, VFund Immo1 469) et de leurs propriétaires.',
    status: 'planned', priority: 'high', progress: 0,
    assignees: ['agt-boris', 'agt-diego'], labels: [L.reprise],
    start_date: '2026-06-23', due_on: '2026-07-18', est: 30, logged: 0,
    mission_id: 'mis-reprise', reporter_id: 'mem-khudolii',
    created_at: '2026-06-05T09:00:00Z', updated_at: '2026-06-05T16:00:00Z',
  }),
  story({
    id: 'st-23a', key: 'LC-23a', title: 'Import Digital Immo (80 lots)',
    phase: 'Planning',
    description_md: 'Sous-tâche : reprise du parc pilote, le plus petit, en premier.',
    status: 'planned', priority: 'medium', progress: 0,
    assignees: ['agt-boris'], labels: [L.reprise],
    start_date: '2026-06-23', due_on: '2026-06-30', est: 8, logged: 0,
    mission_id: 'mis-reprise', parent_id: 'st-23', reporter_id: 'mem-khudolii',
    created_at: '2026-06-05T09:00:00Z', updated_at: '2026-06-05T16:00:00Z',
  }),
  story({
    id: 'st-23b', key: 'LC-23b', title: 'Import ImmoCore (230 lots)',
    phase: 'Planning',
    description_md: 'Sous-tâche : reprise du parc ImmoCore, dont 6 commerciaux.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['agt-diego'], labels: [L.reprise],
    start_date: '2026-07-06', due_on: '2026-07-18', est: 12, logged: 0,
    mission_id: 'mis-reprise', parent_id: 'st-23', reporter_id: 'mem-khudolii',
    created_at: '2026-06-05T09:00:00Z', updated_at: '2026-06-05T16:00:00Z',
  }),
  story({
    id: 'st-24', key: 'LC-24', title: 'Mapping SPI → Life Connect',
    phase: 'Planning',
    description_md: 'Table de correspondance des données SPI vers le nouveau modèle ; règles de transformation et contrôles.',
    status: 'in_progress', priority: 'high', progress: 25,
    assignees: ['agt-aria', 'mem-khudolii'], labels: [L.reprise, L.technique],
    start_date: '2026-06-02', due_on: '2026-06-27', est: 28, logged: 7,
    mission_id: 'mis-reprise', fixed_cost: 800, reporter_id: 'mem-khudolii',
    created_at: '2026-05-29T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-25', key: 'LC-25', title: 'Reprise VFund Immo1 (469 lots) — plan',
    phase: 'Discovery',
    description_md: 'Plan de reprise du plus gros parc (469 lots, 18 comm.), bascule en dernier.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['mem-khudolii'], labels: [L.reprise],
    start_date: '2026-08-10', due_on: '2026-09-04', est: 16, logged: 0,
    mission_id: 'mis-reprise', depends_on: ['st-23'], reporter_id: 'mem-khudolii',
    created_at: '2026-06-06T09:00:00Z', updated_at: '2026-06-06T16:00:00Z',
  }),

  // ===== mis-bascule (au prochain 1ᵉʳ janvier)
  story({
    id: 'st-26', key: 'LC-26', title: 'Liste des décisions métier à valider',
    phase: 'Discovery',
    description_md: 'Plan comptable, règles de relance, déclarations : la liste des décisions à présenter aux experts dès leur arrivée. Action immédiate n°6.',
    status: 'planned', priority: 'high', progress: 0,
    assignees: ['mem-greg', 'mem-agnes', 'mem-jennifer'], labels: [L.compta, L.doc],
    start_date: '2026-06-23', due_on: '2026-07-10', est: 16, logged: 0,
    mission_id: 'mis-bascule', reporter_id: 'mem-greg',
    created_at: '2026-06-07T09:00:00Z', updated_at: '2026-06-07T16:00:00Z',
  }),
  story({
    id: 'st-27', key: 'LC-27', title: 'PV de reprise & arrêt de l’ancien logiciel',
    phase: 'Discovery',
    description_md: 'Comptes équilibrés, PV de reprise signé, arrêt de SPI sur le parc basculé.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['mem-agnes', 'mem-greg'], labels: [L.compta],
    start_date: '2026-08-24', due_on: '2026-09-11', est: 14, logged: 0,
    mission_id: 'mis-bascule', depends_on: ['st-26'], reporter_id: 'mem-agnes',
    created_at: '2026-06-08T09:00:00Z', updated_at: '2026-06-08T16:00:00Z',
  }),

  // ===== mis-automations (en fond de tâche, sans mobiliser les experts)
  story({
    id: 'st-28', key: 'LC-28', title: 'Détection automatique des erreurs (monitoring)',
    phase: 'Implementation',
    description_md: 'Alertes automatiques sur erreurs runtime ; tableau de bord temps réel. Socle de la correction auto.',
    status: 'done', priority: 'medium', progress: 100,
    assignees: ['agt-otto', 'mem-khudolii'], labels: [L.auto, L.technique],
    start_date: '2026-05-11', due_on: '2026-05-29', est: 20, logged: 21,
    mission_id: 'mis-automations', fixed_cost: 500, reporter_id: 'mem-khudolii',
    created_at: '2026-05-06T09:00:00Z', updated_at: '2026-05-29T16:00:00Z',
  }),
  story({
    id: 'st-29', key: 'LC-29', title: 'Agent de correction auto (prépare & teste le fix)',
    phase: 'Implementation',
    description_md: 'À la détection d’une erreur, un agent IA prépare et teste la correction ; validée avant mise en production.',
    status: 'in_progress', priority: 'high', progress: 40,
    assignees: ['agt-otto', 'agt-tess'], labels: [L.auto],
    start_date: '2026-05-25', due_on: '2026-06-10', est: 24, logged: 10,
    mission_id: 'mis-automations', depends_on: ['st-28'], reporter_id: 'mem-khudolii',
    created_at: '2026-05-20T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),
  story({
    id: 'st-30', key: 'LC-30', title: 'Relances automatiques des impayés',
    phase: 'Planning',
    description_md: 'Scénario de relance (J+5 / J+15 / mise en demeure) déclenché par le rapprochement bancaire.',
    status: 'backlog', priority: 'medium', progress: 0,
    assignees: ['agt-cassandre'], labels: [L.auto, L.compta],
    start_date: '2026-07-13', due_on: '2026-07-31', est: 18, logged: 0,
    mission_id: 'mis-automations', depends_on: ['st-8'], reporter_id: 'mem-jennifer',
    created_at: '2026-06-09T09:00:00Z', updated_at: '2026-06-09T16:00:00Z',
  }),

  // ===== mis-support (triage, correction, astreinte)
  story({
    id: 'st-31', key: 'LC-31', title: 'Triage des tickets entrants',
    phase: 'Review',
    description_md: 'Qualification + priorisation des tickets (bug / évolution / question), routage vers la bonne squad.',
    status: 'in_progress', priority: 'medium', progress: 60,
    assignees: ['agt-theo'], labels: [L.bug],
    start_date: '2026-06-01', due_on: '2026-06-30', est: 16, logged: 9,
    mission_id: 'mis-support', reporter_id: 'mem-jennifer',
    created_at: '2026-05-28T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-32', key: 'LC-32', title: 'Bug : incohérence appels de loyers (Digital Immo)',
    phase: 'Review',
    description_md: 'Montant d’appel erroné sur les lots avec charges provisionnelles. Détecté en gestion parallèle.',
    status: 'in_review', priority: 'urgent', progress: 80,
    assignees: ['agt-theo', 'agt-boris'], labels: [L.bug, L.compta],
    start_date: '2026-06-03', due_on: '2026-06-09', est: 8, logged: 7,
    mission_id: 'mis-support', depends_on: ['st-5'], reporter_id: 'agt-theo',
    created_at: '2026-06-02T09:00:00Z', updated_at: '2026-06-11T16:00:00Z',
  }),
  story({
    id: 'st-33', key: 'LC-33', title: 'Bug : arrondi sur régularisation des charges',
    phase: 'Review',
    description_md: 'Écart d’un centime sur la ventilation des charges par lot ; correction préparée par l’astreinte.',
    status: 'in_progress', priority: 'high', progress: 50,
    assignees: ['agt-otto', 'agt-tess'], labels: [L.bug, L.compta],
    start_date: '2026-06-08', due_on: '2026-06-18', est: 6, logged: 3,
    mission_id: 'mis-support', reporter_id: 'agt-theo',
    created_at: '2026-06-06T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
  story({
    id: 'st-34', key: 'LC-34', title: 'Astreinte production — semaine',
    phase: 'Review',
    description_md: 'Routine : surveillance, premiers diagnostics, escalade. Sans mobiliser les experts.',
    status: 'in_progress', priority: 'low', progress: 50,
    assignees: ['agt-otto'], labels: [L.technique],
    start_date: '2026-06-09', due_on: '2026-06-15', est: 10, logged: 5,
    mission_id: 'mis-support', reporter_id: 'mem-greg',
    created_at: '2026-06-08T09:00:00Z', updated_at: '2026-06-12T16:00:00Z',
  }),
];

// --- Selectors -------------------------------------------------------------

const STATUS_ORDER = ['backlog', 'planned', 'in_progress', 'blocked', 'in_review', 'done'];
const PRIORITY_ORDER = ['low', 'medium', 'high', 'urgent'];

/** Count of stories per status, in canonical order. → [{ status, count }]. */
export function storiesByStatus(stories = mockStories) {
  return STATUS_ORDER.map((status) => ({
    status,
    count: stories.filter((s) => s.status === status).length,
  }));
}

/** Count of stories per priority, in canonical order. → [{ priority, count }]. */
export function storiesByPriority(stories = mockStories) {
  return PRIORITY_ORDER.map((priority) => ({
    priority,
    count: stories.filter((s) => s.priority === priority).length,
  }));
}

/** Direct children of a parent story. */
export function subtasksOf(parentId, stories = mockStories) {
  return stories.filter((s) => s.parent_id === parentId);
}

/** Stories with no parent (the gantt/grid roots). */
export function topLevelStories(stories = mockStories) {
  return stories.filter((s) => !s.parent_id);
}

/** Logged hours for a story (seconds → hours). */
export function loggedHours(story) {
  return Math.round((story.logged_seconds / 3600) * 10) / 10;
}

/** Labor cost of a story: logged_hours × blended assignee rate, + fixed_cost. */
export function storyCost(story) {
  const hrs = loggedHours(story);
  const rates = story.assignees.map((a) => a.hourly_rate);
  const blended = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
  return Math.round(hrs * blended) + (story.fixed_cost ?? 0);
}

/** Estimated cost of a story: estimated_hours × blended assignee rate, + fixed_cost. */
export function storyEstimatedCost(story) {
  const rates = story.assignees.map((a) => a.hourly_rate);
  const blended = rates.length ? rates.reduce((a, b) => a + b, 0) / rates.length : 0;
  return Math.round(story.estimated_hours * blended) + (story.fixed_cost ?? 0);
}

/** Project-wide derived totals (estimated vs logged hours, cost rollups). */
export function projectTotals(stories = mockStories) {
  const estimatedHours = stories.reduce((sum, s) => sum + s.estimated_hours, 0);
  const loggedHrs = stories.reduce((sum, s) => sum + loggedHours(s), 0);
  const estimatedCost = stories.reduce((sum, s) => sum + storyEstimatedCost(s), 0);
  const actualCost = stories.reduce((sum, s) => sum + storyCost(s), 0);
  return {
    storyCount: stories.length,
    estimatedHours: Math.round(estimatedHours * 10) / 10,
    loggedHours: Math.round(loggedHrs * 10) / 10,
    estimatedCost,
    actualCost,
    currency: 'EUR',
  };
}

/** Cost rollup grouped by member → [{ member, loggedHours, cost }]. */
export function costByMember(stories = mockStories) {
  const acc = new Map();
  for (const story of stories) {
    const hrs = loggedHours(story);
    if (story.assignees.length === 0) continue;
    const share = hrs / story.assignees.length;
    for (const member of story.assignees) {
      const row = acc.get(member.id) ?? { member, loggedHours: 0, cost: 0 };
      row.loggedHours = Math.round((row.loggedHours + share) * 10) / 10;
      row.cost += Math.round(share * member.hourly_rate);
      acc.set(member.id, row);
    }
  }
  return mockMembers.map((m) => acc.get(m.id)).filter(Boolean);
}

/** Cost rollup grouped by mission → [{ mission, estimatedCost, actualCost }]. */
export function costByMission(stories = mockStories) {
  return mockMissions.map((mission) => {
    const own = stories.filter((s) => s.mission_id === mission.id);
    return {
      mission,
      storyCount: own.length,
      estimatedCost: own.reduce((sum, s) => sum + storyEstimatedCost(s), 0),
      actualCost: own.reduce((sum, s) => sum + storyCost(s), 0),
    };
  });
}
